import {
  APIComponentAttributes,
  APIFieldAttributes,
  APIFieldType,
} from '@ardoq/api-types';
import { isArdoqError, isTruthy, type Result } from '@ardoq/common-helpers';
import { fieldOperations } from '@ardoq/core';
import { localeCompare, type Locale } from '@ardoq/locale';
import { enhancedScopeDataOperations } from '@ardoq/scope-data';
import { inventoryDatasourceOperations } from '../../datasourceOperations';
import { SelectOption } from '@ardoq/select';
import { ColDef, GridApi } from 'ag-grid-enterprise';
import { uniq } from 'lodash';
import {
  cellRendererPropsOperations,
  type FieldEditabilityCheckParams,
} from '../../CellRenderers/cellRendererPropsOperations';
import type {
  BulkEditAffectedComponentCount,
  RuleBasedEditFieldColumn,
  RuleBasedEditFieldInputType,
  RuleBasedEditFieldData,
  RuleBasedEditFieldValue,
  ColumnDefinitionArdoqMetadata,
  FieldColumnDefinitionArdoqMetadata,
  InventoryContext,
} from '../../types';

export type BulkEditFieldFormState = Omit<
  RuleBasedEditFieldData,
  'fieldType' | 'numberOfComponents'
>;

export type AffectedComponentCount =
  | { status: 'loading' }
  | { status: 'loaded'; counts: BulkEditAffectedComponentCount }
  | { status: 'error' };

const toAffectedComponentCount = (
  result: Result<BulkEditAffectedComponentCount>
): AffectedComponentCount =>
  isArdoqError(result)
    ? { status: 'error' }
    : { status: 'loaded', counts: result };

/**
 * The number to put in front of the user, or `null` while we do not have one.
 * Both settings of "only fill empty cells" are answered by the same response,
 * so the toggle picks rather than re-counts.
 */
const getCount = (
  affectedComponentCount: AffectedComponentCount,
  shouldOnlyFillEmptyCells: boolean
): number | null => {
  if (affectedComponentCount.status !== 'loaded') {
    return null;
  }
  const { all, onlyEmpty } = affectedComponentCount.counts;
  return shouldOnlyFillEmptyCells ? onlyEmpty : all;
};

/**
 * Columns whose value identifies another entity rather than carrying a field
 * value of its own. They have their own dialogs — bulk editing them from a
 * plain text or select input would write a name where an id belongs.
 */
const COLUMN_KEYS_WITHOUT_A_FIELD_VALUE = new Set([
  'name',
  'rootWorkspace',
  'parent',
  'type',
]);

/**
 * `null` for the field types this dialog has no input for: a file is an
 * attachment rather than a value, and a date range is a pair of values rather
 * than one.
 */
const toInputType = (
  metadata: FieldColumnDefinitionArdoqMetadata
): RuleBasedEditFieldInputType | null => {
  switch (metadata.reportDataType) {
    case APIFieldType.CHECKBOX:
      return 'checkbox';
    case APIFieldType.SELECT_MULTIPLE_LIST:
      return 'multiselect';
    case APIFieldType.LIST:
    case APIFieldType.USER:
      return 'select';
    case APIFieldType.NUMBER:
      return 'number';
    case APIFieldType.TEXT_AREA:
      return 'textArea';
    case APIFieldType.TEXT:
    case APIFieldType.EMAIL:
    case APIFieldType.URL:
      return 'text';
    case APIFieldType.DATE_ONLY:
      return 'date';
    case APIFieldType.DATE_TIME:
      return 'dateTime';
    case APIFieldType.FILE:
    case APIFieldType.DATE_ONLY_RANGE:
    case APIFieldType.DATE_TIME_RANGE:
      return null;
    default:
      metadata.reportDataType satisfies never;
      return null;
  }
};

const toFieldColumnMetadata = (
  colDef: ColDef<APIComponentAttributes>
): FieldColumnDefinitionArdoqMetadata | null => {
  const metadata = colDef.context as ColumnDefinitionArdoqMetadata | undefined;
  return metadata?.inventoryColumnType === 'field' ? metadata : null;
};

const isBulkEditableColumn = (params: FieldEditabilityCheckParams): boolean => {
  const metadata = toFieldColumnMetadata(params.colDef);
  const field = params.colDef.field;

  if (!metadata || !field || COLUMN_KEYS_WITHOUT_A_FIELD_VALUE.has(field)) {
    return false;
  }

  if (cellRendererPropsOperations.isReadOnlyField(params)) {
    return false;
  }

  if (cellRendererPropsOperations.isDerivedField(params)) {
    return false;
  }

  if (cellRendererPropsOperations.isCalculatedField(params)) {
    return false;
  }

  return toInputType(metadata) !== null;
};

const toUserOption = ({
  name,
  email,
  _id,
}: InventoryContext['users'][number]): SelectOption<string> => ({
  label: `${name} (${email})`,
  value: _id,
});

/**
 * The fields the edit could actually reach: one per model behind the
 * datasource's workspaces. Datasources that name no workspaces
 * (`myApplicationType`, `dataset`), and models scope data has not loaded yet,
 * leave us with every field of that name instead.
 */
const getFieldsWithinReachOfTheEdit = (
  context: InventoryContext,
  columnKey: string
): APIFieldAttributes[] => {
  const fields = enhancedScopeDataOperations.getFieldsByName(
    context.scopeData,
    columnKey
  );
  const workspaceIds =
    inventoryDatasourceOperations.getWorkspaceIdsFromDatasource(
      context.datasourceSelection
    );
  if (workspaceIds.length === 0) {
    return fields;
  }
  const modelIds = new Set(
    workspaceIds
      .map(workspaceId =>
        enhancedScopeDataOperations.getModelIdByWorkspaceId(
          context.scopeData,
          workspaceId
        )
      )
      .filter(isTruthy)
  );
  const fieldsInReach = fields.filter(field => modelIds.has(field.model));
  return fieldsInReach.length > 0 ? fieldsInReach : fields;
};

/**
 * The same field can belong to several models, each with its own list values,
 * and one bulk edit writes the chosen value to components of all of them. Only
 * the values every model accepts are safe to offer — a union would let the
 * user write a value some workspaces do not allow.
 *
 * A model whose copy of the field carries no list values at all is left out of
 * the intersection: that is missing data rather than a model that accepts
 * nothing, and letting it through would disable bulk editing of the field.
 */
const getListFieldOptions = (
  context: InventoryContext,
  columnKey: string
): SelectOption<string>[] => {
  const optionsPerModel = getFieldsWithinReachOfTheEdit(context, columnKey)
    // An empty `defaultValue` splits to [''], which is no list value at all.
    .map(field =>
      fieldOperations.getListFieldOptions(field).filter(value => value !== '')
    )
    .filter(options => options.length > 0);

  const [firstModelOptions, ...otherModels] = optionsPerModel;
  if (!firstModelOptions) {
    return [];
  }

  return uniq(firstModelOptions)
    .filter(value => otherModels.every(options => options.includes(value)))
    .map(value => ({ label: value, value }));
};

const getSelectOptions = (
  context: InventoryContext,
  columnKey: string,
  metadata: FieldColumnDefinitionArdoqMetadata,
  inputType: RuleBasedEditFieldInputType,
  locale: Locale
): SelectOption<string>[] => {
  if (inputType !== 'select' && inputType !== 'multiselect') {
    return [];
  }
  if (metadata.reportDataType === APIFieldType.USER) {
    return context.users
      .map(toUserOption)
      .sort((a, b) => localeCompare(a.label, b.label, locale));
  }
  return getListFieldOptions(context, columnKey);
};

/**
 * A date range default is a pair rather than a value, and list fields keep
 * their options in `defaultValue` rather than a default, so neither reaches
 * this. An empty string is no default either.
 */
const toDefaultFieldValue = (
  defaultValue: APIFieldAttributes['defaultValue']
): RuleBasedEditFieldValue => {
  if (typeof defaultValue === 'string') {
    return defaultValue === '' ? null : defaultValue;
  }
  if (typeof defaultValue === 'number' || typeof defaultValue === 'boolean') {
    return defaultValue;
  }
  return null;
};

/**
 * Only offered when every model in reach agrees, so the option can say what it
 * would write. Models that disagree leave the user no way to know, so the
 * option is dropped rather than shown unnamed.
 */
const getDefaultValue = (
  context: InventoryContext,
  columnKey: string,
  metadata: FieldColumnDefinitionArdoqMetadata
): RuleBasedEditFieldValue => {
  if (
    metadata.reportDataType === APIFieldType.LIST ||
    metadata.reportDataType === APIFieldType.SELECT_MULTIPLE_LIST
  ) {
    return null;
  }
  const defaultValues = uniq(
    getFieldsWithinReachOfTheEdit(context, columnKey).map(field =>
      toDefaultFieldValue(field.defaultValue)
    )
  );
  const [defaultValue] = defaultValues;
  return defaultValues.length === 1 ? defaultValue : null;
};

const toBulkEditFieldColumn = (
  colDef: ColDef<APIComponentAttributes>,
  context: InventoryContext,
  locale: Locale
): RuleBasedEditFieldColumn | null => {
  const metadata = toFieldColumnMetadata(colDef);
  const columnKey = colDef.field;
  const inputType = metadata && toInputType(metadata);

  if (!metadata || !columnKey || !inputType) {
    return null;
  }

  return {
    fieldName: columnKey,
    fieldType: metadata.reportDataType,
    label: colDef.headerName ?? columnKey,
    inputType,
    options: getSelectOptions(context, columnKey, metadata, inputType, locale),
    defaultValue: getDefaultValue(context, columnKey, metadata),
  };
};

const getBulkEditableColumns = (
  api: GridApi<APIComponentAttributes>,
  context: InventoryContext,
  locale: Locale
): RuleBasedEditFieldColumn[] =>
  (api.getColumns() ?? [])
    .map(column => column.getColDef())
    .filter(colDef => isBulkEditableColumn({ colDef, context }))
    .map(colDef => toBulkEditFieldColumn(colDef, context, locale))
    .filter(isTruthy)
    .sort((a, b) => localeCompare(a.label, b.label, locale));

const getEmptyValue = (
  inputType: RuleBasedEditFieldInputType
): RuleBasedEditFieldValue => (inputType === 'multiselect' ? [] : null);

/**
 * Nothing worth writing to every matched component.
 *
 * `NumberInput` reports a cleared field as `NaN`, and a typed `1e999` as
 * `Infinity`. Whitespace-only text is empty too: written to a cell it reads as
 * blank in the grid but no longer counts as empty, so it would quietly put the
 * cell beyond the reach of later "only fill empty cells" runs.
 */
const isEmptyValue = (value: RuleBasedEditFieldValue): boolean => {
  if (typeof value === 'number') {
    return !Number.isFinite(value);
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  return value === null || (Array.isArray(value) && value.length === 0);
};

/**
 * Clearing a field and only filling empty cells cancel each other out, so the
 * narrowing is dropped rather than left to produce a no-op.
 */
const isOnlyFillingEmptyCells = ({
  selection,
  shouldOnlyFillEmptyCells,
}: BulkEditFieldFormState): boolean =>
  selection.mode !== 'empty' && shouldOnlyFillEmptyCells;

/**
 * Surrounding whitespace is no more intentional than a whitespace-only value,
 * and trimming it on submit rather than on change leaves the user free to type
 * spaces mid-value.
 *
 * The field's kind and the count the user acted on travel with the edit: they
 * are what tracking reports of it, and this is the last place that holds both.
 */
const toSubmittedUpdate = (
  update: BulkEditFieldFormState,
  column: RuleBasedEditFieldColumn,
  count: number | null
): RuleBasedEditFieldData => {
  const { selection } = update;
  const trimmed: BulkEditFieldFormState =
    selection.mode === 'value' && typeof selection.value === 'string'
      ? {
          ...update,
          selection: { ...selection, value: selection.value.trim() },
        }
      : update;
  return {
    ...trimmed,
    shouldOnlyFillEmptyCells: isOnlyFillingEmptyCells(trimmed),
    fieldType: column.fieldType,
    numberOfComponents: count ?? 0,
  };
};

/**
 * A bulk write whose scope we cannot state is not one to let through, so the
 * count has to be in before the edit can be submitted. Clearing a field and
 * setting it to its default need no value of their own.
 */
const isSubmittable = (
  formState: BulkEditFieldFormState,
  affectedComponentCount: AffectedComponentCount
): boolean => {
  const count = getCount(
    affectedComponentCount,
    isOnlyFillingEmptyCells(formState)
  );
  const { selection } = formState;
  const hasSomethingToWrite =
    selection.mode !== 'value' || !isEmptyValue(selection.value);
  return count !== null && count > 0 && hasSomethingToWrite;
};

export const bulkEditFieldOperations = {
  isBulkEditableColumn,
  getBulkEditableColumns,
  toBulkEditFieldColumn,
  toInputType,
  getEmptyValue,
  isEmptyValue,
  isOnlyFillingEmptyCells,
  toSubmittedUpdate,
  toAffectedComponentCount,
  getCount,
  isSubmittable,
};
